import {
  Association,
  EvaluationPeriod,
  JobCandidateHiringStep,
  JobCandidateStatus,
  JobCandidateStatusReason,
  JobEndTimeframe,
  JobStartTimeframe,
  LocationPreference,
  SourcingChannel,
} from '@/gql/graphql'
import { $t } from '@/composables/translation'

export const locationPreferenceOptions = [
  { value: LocationPreference.OnsiteOnly, label: $t('On site') },
  { value: LocationPreference.OnsiteSome, label: $t('Hybrid (some days in-person)') },
  { value: LocationPreference.RemoteOnly, label: $t('Fully Remote') },
]

export const startDateTimeframeOptions = [
  { value: JobStartTimeframe.Asap, label: $t('ASAP') },
  { value: JobStartTimeframe.NextMonth, label: $t('Within a Month') },
  { value: JobStartTimeframe.OnDate, label: $t('On a Specific Date') },
]

export const associationOptions = [
  { value: Association.FullTime, label: $t('Full-Time Contractor') },
  { value: Association.PartTime, label: $t('Part-Time/Flexible Days') },
  { value: Association.Project, label: $t('One-Time Project') },
]

export const endDateTimeframeOptions = [
  { value: JobEndTimeframe.OneMonth, label: $t('1 Month') },
  { value: JobEndTimeframe.ThreeMonths, label: $t('3 Months') },
  { value: JobEndTimeframe.SixMonths, label: $t('6 Months') },
  { value: JobEndTimeframe.OnDate, label: $t('Until Date') },
]

export const evaluationPeriodOptions = [
  { value: EvaluationPeriod.Quick, label: $t('Quickly and ongoing') },
  { value: EvaluationPeriod.Week, label: $t('Weekly') },
  { value: EvaluationPeriod.Month, label: $t('Within a month') },
]

// Hire flows
export enum JOB_FLOW {
  BROADCAST = 'broadcast',
  DIRECT_HIRE = 'direct-hire',
  BRIEF = 'brief',
}

export enum JOB_TAB {
  OVERVIEW = 'overview',
  DETAILS = 'details',
  BUDGET = 'budget',
  SETTINGS = 'settings',
}

export enum BUDGET_TYPE {
  SPECIFIC_RATE = 'specific-rate',
  RATE_RANGE = 'rate-range',
}

export const JobCandidateStatusList = {
  [JobCandidateStatus.Eligible]: {
    label: $t('Eligible'),
    value: JobCandidateStatus.Eligible,
  },
  [JobCandidateStatus.NonEligible]: {
    label: $t('Non-Eligible'),
    value: JobCandidateStatus.NonEligible,
  },
  [JobCandidateStatus.Duplicate]: {
    label: $t('Duplicate'),
    value: JobCandidateStatus.Duplicate,
  },
}

export const JobCandidateStatusListArray = [
  JobCandidateStatusList[JobCandidateStatus.Eligible],
  JobCandidateStatusList[JobCandidateStatus.NonEligible],
  JobCandidateStatusList[JobCandidateStatus.Duplicate],
]

export const JobCandidateStepList: Record<
  JobCandidateHiringStep,
  { label: string; value: JobCandidateHiringStep }
> = {
  // System steps
  [JobCandidateHiringStep.Shortlisted]: {
    label: $t('Shortlisted'),
    value: JobCandidateHiringStep.Shortlisted,
  },
  [JobCandidateHiringStep.InvitedToApply]: {
    label: $t('Invited to apply'),
    value: JobCandidateHiringStep.InvitedToApply,
  },
  [JobCandidateHiringStep.Applied]: {
    label: $t('Applied'),
    value: JobCandidateHiringStep.Applied,
  },
  [JobCandidateHiringStep.Hiring]: {
    label: $t('Hiring'),
    value: JobCandidateHiringStep.Hiring,
  },
  // Selection phase
  [JobCandidateHiringStep.SelectionSubmitted]: {
    label: $t('Submitted'),
    value: JobCandidateHiringStep.SelectionSubmitted,
  },
  [JobCandidateHiringStep.SelectionUnderReview]: {
    label: $t('Under Review (MSP)'),
    value: JobCandidateHiringStep.SelectionUnderReview,
  },
  [JobCandidateHiringStep.SelectionShortlisted]: {
    label: $t('Shortlisted'),
    value: JobCandidateHiringStep.SelectionShortlisted,
  },
  [JobCandidateHiringStep.SelectionDeclinedByMsp]: {
    label: $t('Declined by MSP'),
    value: JobCandidateHiringStep.SelectionDeclinedByMsp,
  },
  [JobCandidateHiringStep.SelectionWithdrawn]: {
    label: $t('Withdrawn'),
    value: JobCandidateHiringStep.SelectionWithdrawn,
  },
  [JobCandidateHiringStep.SelectionPresentedToClient]: {
    label: $t('Presented to Client'),
    value: JobCandidateHiringStep.SelectionPresentedToClient,
  },
  // Interview phase
  [JobCandidateHiringStep.InterviewSelected]: {
    label: $t('Selected for Interview'),
    value: JobCandidateHiringStep.InterviewSelected,
  },
  [JobCandidateHiringStep.InterviewScheduled]: {
    label: $t('Interview Scheduled'),
    value: JobCandidateHiringStep.InterviewScheduled,
  },
  [JobCandidateHiringStep.InterviewCompleted]: {
    label: $t('Interview Completed'),
    value: JobCandidateHiringStep.InterviewCompleted,
  },
  [JobCandidateHiringStep.InterviewWithdrawn]: {
    label: $t('Withdrawn'),
    value: JobCandidateHiringStep.InterviewWithdrawn,
  },
  [JobCandidateHiringStep.InterviewShortlisted]: {
    label: $t('Shortlisted'),
    value: JobCandidateHiringStep.InterviewShortlisted,
  },
  // Completion phase
  [JobCandidateHiringStep.CompletionRejectedByClient]: {
    label: $t('Rejected by Client'),
    value: JobCandidateHiringStep.CompletionRejectedByClient,
  },
  [JobCandidateHiringStep.CompletionDeclinedByMsp]: {
    label: $t('Declined by MSP'),
    value: JobCandidateHiringStep.CompletionDeclinedByMsp,
  },
  [JobCandidateHiringStep.CompletionWithdrawn]: {
    label: $t('Withdrawn'),
    value: JobCandidateHiringStep.CompletionWithdrawn,
  },
  [JobCandidateHiringStep.CompletionOfferExtended]: {
    label: $t('Offer Extended'),
    value: JobCandidateHiringStep.CompletionOfferExtended,
  },
  [JobCandidateHiringStep.CompletionCandidateOnboarding]: {
    label: $t('Candidate Onboarding'),
    value: JobCandidateHiringStep.CompletionCandidateOnboarding,
  },
  [JobCandidateHiringStep.CompletionBackgroundCheck]: {
    label: $t('Background Check / Screening'),
    value: JobCandidateHiringStep.CompletionBackgroundCheck,
  },
  [JobCandidateHiringStep.CompletionHired]: {
    label: $t('Hired / Filled'),
    value: JobCandidateHiringStep.CompletionHired,
  },
  [JobCandidateHiringStep.CompletionOnHold]: {
    label: $t('On Hold'),
    value: JobCandidateHiringStep.CompletionOnHold,
  },
  [JobCandidateHiringStep.CompletionTalentpool]: {
    label: $t('Talentpool'),
    value: JobCandidateHiringStep.CompletionTalentpool,
  },
}

export const JobCandidateStepListArray = [
  JobCandidateStepList[JobCandidateHiringStep.Shortlisted],
  JobCandidateStepList[JobCandidateHiringStep.InvitedToApply],
  JobCandidateStepList[JobCandidateHiringStep.Applied],
  JobCandidateStepList[JobCandidateHiringStep.Hiring],
]

export const SYSTEM_STEPS = new Set([
  JobCandidateHiringStep.Shortlisted,
  JobCandidateHiringStep.InvitedToApply,
  JobCandidateHiringStep.Applied,
  JobCandidateHiringStep.Hiring,
])

export const SELECTION_PHASE_STEPS = new Set([
  JobCandidateHiringStep.SelectionSubmitted,
  JobCandidateHiringStep.SelectionUnderReview,
  JobCandidateHiringStep.SelectionShortlisted,
  JobCandidateHiringStep.SelectionDeclinedByMsp,
  JobCandidateHiringStep.SelectionWithdrawn,
  JobCandidateHiringStep.SelectionPresentedToClient,
])

export const INTERVIEW_PHASE_STEPS = new Set([
  JobCandidateHiringStep.InterviewSelected,
  JobCandidateHiringStep.InterviewScheduled,
  JobCandidateHiringStep.InterviewCompleted,
  JobCandidateHiringStep.InterviewWithdrawn,
  JobCandidateHiringStep.InterviewShortlisted,
])

export const COMPLETION_PHASE_STEPS = new Set([
  JobCandidateHiringStep.CompletionRejectedByClient,
  JobCandidateHiringStep.CompletionDeclinedByMsp,
  JobCandidateHiringStep.CompletionWithdrawn,
  JobCandidateHiringStep.CompletionOfferExtended,
  JobCandidateHiringStep.CompletionCandidateOnboarding,
  JobCandidateHiringStep.CompletionBackgroundCheck,
  JobCandidateHiringStep.CompletionHired,
  JobCandidateHiringStep.CompletionOnHold,
  JobCandidateHiringStep.CompletionTalentpool,
])

export const JobCandidateManualStepsByPhase = {
  [$t('Selection')]: [...SELECTION_PHASE_STEPS].map((step) => JobCandidateStepList[step]),
  [$t('Interview')]: [...INTERVIEW_PHASE_STEPS].map((step) => JobCandidateStepList[step]),
  [$t('Completion')]: [...COMPLETION_PHASE_STEPS].map((step) => JobCandidateStepList[step]),
}

export const JobCandidateAllStepListArray = Object.values(JobCandidateStepList)

export const SourcingChannelList = {
  [SourcingChannel.Marketplace]: {
    label: $t('Marketplace'),
    value: SourcingChannel.Marketplace,
  },
  [SourcingChannel.TrustedContact]: {
    label: $t('Trusted Contact'),
    value: SourcingChannel.TrustedContact,
  },
  [SourcingChannel.CandidateSubmission]: {
    label: $t('Candidate Submission'),
    value: SourcingChannel.CandidateSubmission,
  },
  [SourcingChannel.RecruiterAttribution]: {
    label: $t('Recruiter Attribution'),
    value: SourcingChannel.RecruiterAttribution,
  },
}

export const SourcingChannelListArray = [
  SourcingChannelList[SourcingChannel.Marketplace],
  SourcingChannelList[SourcingChannel.TrustedContact],
  SourcingChannelList[SourcingChannel.CandidateSubmission],
  SourcingChannelList[SourcingChannel.RecruiterAttribution],
]

export const JobCandidateStatusReasonList = {
  [JobCandidateStatusReason.SkillsOrExperience]: {
    label: $t('Skills or experience'),
    value: JobCandidateStatusReason.SkillsOrExperience,
  },
  [JobCandidateStatusReason.SpecificJobCriteria]: {
    label: $t('Specific job criteria'),
    value: JobCandidateStatusReason.SpecificJobCriteria,
  },
  [JobCandidateStatusReason.Availability]: {
    label: $t('Availability'),
    value: JobCandidateStatusReason.Availability,
  },
  [JobCandidateStatusReason.MarketEligibility]: {
    label: $t('Market eligibility'),
    value: JobCandidateStatusReason.MarketEligibility,
  },
  [JobCandidateStatusReason.Budget]: {
    label: $t('Budget'),
    value: JobCandidateStatusReason.Budget,
  },
  [JobCandidateStatusReason.AcceptedOtherJob]: {
    label: $t('Accepted other job'),
    value: JobCandidateStatusReason.AcceptedOtherJob,
  },
  [JobCandidateStatusReason.OtherCandidateAccepted]: {
    label: $t('Other candidate accepted'),
    value: JobCandidateStatusReason.OtherCandidateAccepted,
  },
  [JobCandidateStatusReason.AddedByMistake]: {
    label: $t('Added by mistake'),
    value: JobCandidateStatusReason.AddedByMistake,
  },
  [JobCandidateStatusReason.ExistingApplicationThroughOtherChannels]: {
    label: $t('Existing application through other channels'),
    value: JobCandidateStatusReason.ExistingApplicationThroughOtherChannels,
  },
}

export const JobCandidateStatusReasonListArray = [
  JobCandidateStatusReasonList[JobCandidateStatusReason.SkillsOrExperience],
  JobCandidateStatusReasonList[JobCandidateStatusReason.SpecificJobCriteria],
  JobCandidateStatusReasonList[JobCandidateStatusReason.Availability],
  JobCandidateStatusReasonList[JobCandidateStatusReason.MarketEligibility],
  JobCandidateStatusReasonList[JobCandidateStatusReason.Budget],
  JobCandidateStatusReasonList[JobCandidateStatusReason.AcceptedOtherJob],
  JobCandidateStatusReasonList[JobCandidateStatusReason.OtherCandidateAccepted],
  JobCandidateStatusReasonList[JobCandidateStatusReason.AddedByMistake],
  JobCandidateStatusReasonList[JobCandidateStatusReason.ExistingApplicationThroughOtherChannels],
]
